import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { useCallback } from "react";

// GraphQL Queries and Mutations
const TABLE_COLUMN_DESCRIPTION_QUERIES_AND_MUTATIONS = {
  GET_TABLE_COLUMN_DESCRIPTION: gql`
    query GetTableColumnDescription(
      $user_db_id: Int!
      $table_name: String!
      $column_name: String!
    ) {
      getTableColumnDescription(
        user_db_id: $user_db_id
        table_name: $table_name
        column_name: $column_name
      ) {
        schema_node_id
        type
        name
        data_type
        description
        created_on
        updated_on
      }
    }
  `,

  UPDATE_TABLE_COLUMN_DESCRIPTION: gql`
    mutation UpdateTableColumnDescription(
      $user_db_id: Int!
      $table_name: String!
      $column_name: String!
      $description: String!
    ) {
      updateTableColumnDescription(
        user_db_id: $user_db_id
        table_name: $table_name
        column_name: $column_name
        description: $description
      ) {
        schema_node_id
        type
        name
        data_type
        description
        created_on
        updated_on
      }
    }
  `,
};

export const useTableColumnDescription = () => {
  const [getTableColumnDescriptionQuery, {
    loading: getColumnDescriptionLoading,
    error: getColumnDescriptionError,
    data: getColumnDescriptionData,
  }] = useLazyQuery(
    TABLE_COLUMN_DESCRIPTION_QUERIES_AND_MUTATIONS.GET_TABLE_COLUMN_DESCRIPTION,
    {
      fetchPolicy: 'network-only'
    }
  );

  const [updateTableColumnDescriptionMutation, {
    loading: updateColumnDescriptionLoading,
    error: updateColumnDescriptionError,
  }] = useMutation(
    TABLE_COLUMN_DESCRIPTION_QUERIES_AND_MUTATIONS.UPDATE_TABLE_COLUMN_DESCRIPTION
  );

  const getTableColumnDescription = useCallback(async ({
    userDbId,
    tableName,
    columnName,
  }) => {
    try {
      if (!userDbId || !tableName || !columnName) {
        throw new Error("userDbId, tableName, and columnName are required");
      }

      const { data } = await getTableColumnDescriptionQuery({
        variables: {
          user_db_id: Number(userDbId),
          table_name: tableName,
          column_name: columnName,
        },
      });

      if (!data?.getTableColumnDescription) {
        throw new Error("Failed to fetch column description");
      }

      return {
        success: true,
        data: data.getTableColumnDescription,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "An error occurred while fetching column description",
      };
    }
  }, [getTableColumnDescriptionQuery]);

  const updateTableColumnDescription = useCallback(async ({
    userDbId,
    tableName,
    columnName,
    description,
  }) => {
    try {
      if (!userDbId || !tableName || !columnName || !description) {
        throw new Error("userDbId, tableName, columnName, and description are required");
      }

      const { data } = await updateTableColumnDescriptionMutation({
        variables: {
          user_db_id: Number(userDbId),
          table_name: tableName,
          column_name: columnName,
          description: description.trim(),
        },
        refetchQueries: [{
          query: TABLE_COLUMN_DESCRIPTION_QUERIES_AND_MUTATIONS.GET_TABLE_COLUMN_DESCRIPTION,
          variables: {
            user_db_id: Number(userDbId),
            table_name: tableName,
            column_name: columnName,
          },
        }],
      });

      if (!data?.updateTableColumnDescription) {
        throw new Error("Failed to update column description");
      }

      return {
        success: true,
        data: data.updateTableColumnDescription,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "An error occurred while updating column description",
      };
    }
  }, [updateTableColumnDescriptionMutation]);

  return {
    getTableColumnDescription,
    updateTableColumnDescription,
    loading: getColumnDescriptionLoading,
    error: getColumnDescriptionError,
    data: getColumnDescriptionData?.getTableColumnDescription,
    updating: updateColumnDescriptionLoading,
    updateError: updateColumnDescriptionError,
  };
};

